'use client'

import { useMemo, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import clsx from 'clsx'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { SyneriseFormEvent, syneriseSendFormData } from 'lib/synerise'
import { getQueryClient } from 'shared/api'
import { AddressToDisplayInterface, FormMode } from 'shared/types/forms'
import Button from 'shared/ui/Button'
import {
  ControlledCheckbox
} from 'shared/ui/form/ControlledElements/ControlledCheckbox'
import {
  ControlledHiddenField
} from 'shared/ui/form/ControlledElements/ControlledHiddenField'
import {
  ControlledSelectField
} from 'shared/ui/form/ControlledElements/ControlledSelectField'
import {
  ControlledTextField
} from 'shared/ui/form/ControlledElements/ControlledTextField'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'
import { PostalCodeRegex, TaxIdRegex } from 'shared/utils/validators'

import { useInvoiceAddressPort } from '../../../../ports'
import { extractTokenFromClientSession } from '../../../../session'
import { addInvoiceAddress, editInvoiceAddress } from '../../api'
import { AddressServiceSingleton } from '../../mappers'
import {
  colorsData,
  CustomerAddressType,
  CustomerInvoiceAddressFormValues,
  iconsData,
  InvoiceType,
  ResponseStatus
} from '../../types'
import { IS_PERSONAL_INVOICE_ENABLED } from '../../utils'
import GoogleAddressInput from '../AddressAutocompleteInput'
import { ControlledOptionRow } from '../ControlledOptionRow'

import styles from './FormAddInvoiceAddress.module.scss'

const INVOICE_TYPE_OPTIONS = [
  { value: InvoiceType.company, label: 'Faktura firmowa' },
  { value: InvoiceType.personal, label: 'Faktura imienna' }
]

interface FormAddInvoiceAddressProps {
  mode: FormMode
  handleClose: () => void
  defaultValues: CustomerInvoiceAddressFormValues
}

export const FormAddInvoiceAddress = ({
  defaultValues,
  mode,
  handleClose
}: FormAddInvoiceAddressProps) => {
  const { onInvoiceAddressSaved } = useInvoiceAddressPort()
  const [submitState, setSubmitState] = useState<ResponseStatus>(
    ResponseStatus.initial
  )
  const router = useRouter()
  const pathname = usePathname()
  const session = useSession()
  const token = extractTokenFromClientSession(session)
  const methods = useForm<CustomerInvoiceAddressFormValues>({
    mode: 'onChange',
    defaultValues
  })

  const invoiceType = IS_PERSONAL_INVOICE_ENABLED
    ? methods.watch('invoiceType')
    : InvoiceType.company

  const handleUpdateForm = (payload: AddressToDisplayInterface) => {
    const values = methods.getValues()
    const preparePayload: CustomerInvoiceAddressFormValues = {
      ...values,
      ...payload
    }

    methods.reset(preparePayload)
  }

  const isDefaultAddressDisabled =
    defaultValues.isDefault && mode === FormMode.edit

  const prepareFullAddress = () => {
    const defCompanyName = defaultValues?.companyName
      ? `${defaultValues?.companyName}, `
      : ''
    const defStreet = defaultValues?.street ? `${defaultValues?.street} ` : ''
    const defHouseNumber = defaultValues?.houseNumber
      ? `${defaultValues?.houseNumber},  `
      : ''
    const defCity = defaultValues?.city ? `${defaultValues?.city},  ` : ''
    const defCountry =
      defCompanyName || defStreet || defHouseNumber || defCity ? `Polska` : ''

    return `${defCompanyName}${defStreet}${defHouseNumber}${defCity}${defCountry}`
  }

  const _handleClose = async () => {
    /**
     * Refresh props from getServerSideProps
     * More: https://www.joshwcomeau.com/nextjs/refreshing-server-side-props/
     */
    void router.replace(pathname || '')
    handleClose()
  }

  const onSubmit: SubmitHandler<CustomerInvoiceAddressFormValues> = async values => {
    setSubmitState(ResponseStatus.pending)

    try {
      const payload = AddressServiceSingleton.mapInvoiceFormValuesToPayload(
        values,
        CustomerAddressType.invoice
      )

      if (mode === FormMode.create) {
        const response = await addInvoiceAddress(payload, token)

        if ([200, 201].includes(response.status)) {
          const responseId = response.data.data.id

          if (responseId) {
            void getQueryClient().refetchQueries({
              queryKey: ['invoiceAddresses']
            })

            onInvoiceAddressSaved(responseId, FormMode.create)
          }
          await _handleClose()

          toast.success('Dane zostały zapisane')

          if (payload.invoiceType === InvoiceType.company) {
            syneriseSendFormData({
              formData: {
                invoiceCity: payload.city,
                invoicePostalCode: payload.postalCode,
                invoiceCompanyName: payload.companyName
              },
              event: SyneriseFormEvent.INVOICE_ADDRESS_CREATE
            })
          }

          setSubmitState(ResponseStatus.fulfilled)
        } else {
          setSubmitState(ResponseStatus.rejected)
        }
      } else if (mode === FormMode.edit) {
        const response = await editInvoiceAddress(values.id, payload, token)

        if ([200, 201].includes(response.status)) {
          const responseId = response.data.data.id

          if (responseId) {
            onInvoiceAddressSaved(responseId, FormMode.edit)
          }
          await _handleClose()
          void getQueryClient().refetchQueries({
            queryKey: ['invoiceAddresses']
          })
          toast.success(`Adres "${values.name}" został zaktualizowany.`)

          if (payload.invoiceType === InvoiceType.company) {
            syneriseSendFormData({
              formData: {
                invoiceCity: payload.city,
                invoicePostalCode: payload.postalCode,
                invoiceCompanyName: payload.companyName
              },
              event: SyneriseFormEvent.INVOICE_ADDRESS_EDIT
            })
          }
          setSubmitState(ResponseStatus.fulfilled)
        } else {
          setSubmitState(ResponseStatus.rejected)
        }
      }
    } catch (error) {
      console.error(
        'An error has occurred when creating/editing invoice address:',
        error
      )
      toast.error(getApiErrors(error))
      setSubmitState(ResponseStatus.rejected)
    }
  }

  const nameAndLastNameFields = useMemo(() => {
    return (
      <div className={styles.row} key='personal'>
        <ControlledTextField
          id='invoice_address_first_name'
          label='Imię'
          placeholder='Imię'
          name='firstName'
          required
          rules={{
            required: 'Imię jest wymagane.',
            minLength: {
              value: 3,
              message: 'Min. 3 znaki.'
            },
            maxLength: {
              value: 64,
              message: 'Zbyt długie imię.'
            }
          }}
          noMargin
        />
        <ControlledTextField
          id='invoice_address_last_name'
          label='Nazwisko'
          placeholder='Nazwisko'
          required
          name='lastName'
          rules={{
            required: 'Nazwisko jest wymagane.',
            minLength: {
              value: 3,
              message: 'Min. 3 znaki.'
            },
            maxLength: {
              value: 64,
              message: 'Zbyt długie nazwisko.'
            }
          }}
          noMargin
        />
      </div>
    )
  }, [])

  return (
    <>
      <FormProvider {...methods}>
        <form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
          <ControlledHiddenField name='id' />
          <div className={styles.row}>
            {isDefaultAddressDisabled ? (
              <div className={styles.addressDefaultNotice}>
                Obecnie wybrany podmiot główny
              </div>
            ) : (
              <ControlledCheckbox
                id='invoice_address_default'
                name='isDefault'
                label='Ustaw jako główny podmiot do faktur'
                defaultValue={defaultValues.isDefault}
              />
            )}
          </div>
          <div className={styles.row}>
            <ControlledTextField
              id='invoice_address_name'
              label='Nazwa wpisu'
              placeholder='np. "praca" lub "firmowy"'
              required
              name='name'
              rules={{
                required: 'Nazwa jest wymagana.',
                minLength: {
                  value: 3,
                  message: 'Min. 3 znaki.'
                },
                maxLength: {
                  value: 256,
                  message: 'Przekroczono dopuszczalną długość.'
                }
              }}
              noMargin
            />
            {IS_PERSONAL_INVOICE_ENABLED && (
              <ControlledSelectField
                id='invoice_address_type'
                label='Rodzaj faktury'
                required
                rules={{
                  required: 'Wybierz rodzaj faktury.'
                }}
                name='invoiceType'
                options={INVOICE_TYPE_OPTIONS}
              />
            )}
          </div>
          <div className={styles.row}>
            <ControlledOptionRow
              id='invoice_address_icon'
              type='icons'
              label='Ikona:'
              defaultValue={iconsData[0].value}
              data={iconsData}
              name='iconType'
            />
            <ControlledOptionRow
              id='invoice_address_color'
              type='colors'
              label='Kolor:'
              defaultValue={colorsData[0].value}
              data={colorsData}
              name='iconColor'
            />
          </div>
          <div className={styles.subtitle}>Wyszukaj adres</div>
          <div className={clsx(styles.row, styles.noMargin)}>
            <GoogleAddressInput
              handleUpdateForm={handleUpdateForm}
              prepareFullAddress={prepareFullAddress}
            />
          </div>
          <hr className={styles.hr} />
          <div className={styles.subtitle}>Uzupełnij dane</div>
          {invoiceType === InvoiceType.company ? (
            <>
              {nameAndLastNameFields}
              <div className={styles.row} key='company'>
                <ControlledTextField
                  id='invoice_address_company_name'
                  label='Pełna nazwa firmy'
                  placeholder='Przykładowa Firma Sp. z o.o.'
                  name='companyName'
                  required
                  rules={{
                    required: 'Nazwa jest wymagana.',
                    minLength: {
                      value: 3,
                      message: 'Min. 3 znaki.'
                    },
                    maxLength: {
                      value: 128,
                      message: 'Maks. 128 znaków.'
                    }
                  }}
                  noMargin
                />
                <ControlledTextField
                  id='invoice_address_tax'
                  name='taxId'
                  label='NIP'
                  placeholder='NIP'
                  required
                  rules={{
                    required: 'Numer NIP jest wymagany.',
                    pattern: {
                      value: TaxIdRegex,
                      message: 'Niepoprawny numer NIP.'
                    },
                    validate: (value: string) => {
                      if (value && value.length < 10) {
                        return 'Wymagane 10 cyfr.'
                      }

                      return true
                    }
                  }}
                  noMargin
                />
              </div>
            </>
          ) : (
            nameAndLastNameFields
          )}
          <div className={styles.row}>
            <ControlledTextField
              id='invoice_address_postal'
              label='Kod pocztowy'
              placeholder='xx-xxx'
              name='postalCode'
              required
              rules={{
                required: 'Kod pocztowy jest wymagany.',
                maxLength: {
                  value: 6,
                  message: 'Maks. 6 znaków.'
                },
                pattern: {
                  value: PostalCodeRegex,
                  message: 'Nieprawidłowy format.'
                }
              }}
              noMargin
            />
            <ControlledTextField
              id='invoice_address_city'
              label='Miejscowość'
              placeholder='Nazwa miejscowości'
              name='city'
              required
              rules={{
                required: 'Nazwa jest wymagana.',
                minLength: {
                  value: 3,
                  message: 'Min. 3 znaki.'
                }
              }}
              noMargin
            />
          </div>
          <div className={styles.row}>
            <ControlledTextField
              id='invoice_address_street'
              label='Ulica'
              placeholder='Nazwa ulicy'
              name='street'
              required
              rules={{
                required: 'Nazwa jest wymagana.',
                minLength: {
                  value: 3,
                  message: 'Min. 3 znaki.'
                }
              }}
              noMargin
            />
            <ControlledTextField
              id='invoice_address_house_number'
              label='Numer budynku'
              placeholder='Numer budynku'
              name='houseNumber'
              required
              rules={{
                required: 'Numer jest wymagany.',
                maxLength: {
                  value: 12,
                  message: 'Maks. 12 znaków.'
                }
              }}
              noMargin
            />
            <ControlledTextField
              id='invoice_address_flat_number'
              label='Numer lokalu'
              placeholder='Numer lokalu'
              name='flatNumber'
              rules={{
                maxLength: {
                  value: 6,
                  message: 'Maks. 6 znaków.'
                }
              }}
              noMargin
            />
          </div>
          <div className={styles.row}>
            <Button
              id='invoice_address_save_button'
              disabled={submitState === ResponseStatus.pending}
              type='submit'
              variant='primary'
              size='large'
              fullWidth
            >
              {submitState === ResponseStatus.pending ? 'Wysyłam...' : 'Zapisz'}
            </Button>
            <Button
              id='invoice_address_cancel_button'
              variant='neutralOutline'
              size='large'
              type='button'
              onClick={handleClose}
              fullWidth
            >
              Anuluj
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  )
}
